/**
 * Hook for the "Data File Variables" overview.
 *
 * Fetches the datamap (generated server-side from the SAVED questionnaire),
 * parses it into variables grouped by section, and exposes the two export-style
 * controls (variable naming + variable style). The datamap is the canonical
 * source of "what the authored labels become in the final data file": every
 * question and hidden question appears as a variable with its generated code and
 * value codes; lists / range lists / exceptions are absent (they aren't
 * variables).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useEngine } from "hooks/api/useEngine"
import {
  EXPORT_VARIABLE_TYPES,
  VARIABLE_STYLE,
} from "types/engineExport.types"
import { ExportMapVariable, ExportMapGroup } from "types/exportMap.types"
import {
  parseExportMapCSV,
  groupVariables,
} from "features/QueryConsole/components/ExportMap/utils/exportMapParser"
import { notify } from "shared/ui/Notifications/notify"

interface UseDataFileVariablesProps {
  /** Project identifier */
  projectId: string
  /** Currently selected variant name ("" for master) */
  currentVariant: string
  /** Whether the Data File Variables tab is currently active */
  isActive: boolean
}

interface UseDataFileVariablesResult {
  varType: EXPORT_VARIABLE_TYPES
  setVarType: (value: EXPORT_VARIABLE_TYPES) => void
  labelStyle: VARIABLE_STYLE
  setLabelStyle: (value: VARIABLE_STYLE) => void
  variables: ExportMapVariable[]
  groups: ExportMapGroup[]
  rootVariables: ExportMapVariable[]
  isLoading: boolean
  error: string | null
  refetch: () => void
}

export const useDataFileVariables = ({
  projectId,
  currentVariant,
  isActive,
}: UseDataFileVariablesProps): UseDataFileVariablesResult => {
  const { getDatamapCSV, isFetchingDatamapCSV } = useEngine()

  const [varType, setVarType] = useState<EXPORT_VARIABLE_TYPES>(
    EXPORT_VARIABLE_TYPES.NUMBERING
  )
  const [labelStyle, setLabelStyle] = useState<VARIABLE_STYLE>(
    VARIABLE_STYLE.DEFAULT
  )
  const [csv, setCsv] = useState<string>("")
  const [error, setError] = useState<string | null>(null)

  // Guard so we only fetch once per unique (project, variant, varType, style) key
  const lastKeyRef = useRef<string | null>(null)

  const doFetch = useCallback(
    async (force = false): Promise<void> => {
      if (!projectId) return

      const key = `${projectId}|${currentVariant}|${varType}|${labelStyle}`
      if (!force && lastKeyRef.current === key) return
      lastKeyRef.current = key

      try {
        setError(null)
        const res = await getDatamapCSV(
          projectId,
          true, // group sections
          varType,
          labelStyle,
          // Do NOT request question text: the overview doesn't display it, and
          // it bloats the payload with multi-line, comma-laden wording. (The
          // parser handles multi-line quoted fields correctly, so this is a
          // relevance/size choice, not a correctness workaround.)
          false,
          currentVariant || undefined
        )
        const raw =
          typeof res.data === "string" ? res.data : String(res.data ?? "")
        // The backend prepends a UTF-8 BOM to CSV exports — strip it.
        setCsv(raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw)
      } catch (err) {
        // Allow a later retry for this key
        lastKeyRef.current = null
        const message =
          err instanceof Error ? err.message : "Failed to load variable codes"
        setError(message)
        notify.error("Could not load data file variables", message)
      }
    },
    [projectId, currentVariant, varType, labelStyle, getDatamapCSV]
  )

  // Fetch when the tab becomes active or when the controls / variant change.
  useEffect(() => {
    if (isActive) {
      doFetch()
    }
  }, [isActive, doFetch])

  // Parsing is memoized — this is the main fix for the modal's previous lag.
  const variables = useMemo<ExportMapVariable[]>(
    () => (csv ? parseExportMapCSV(csv) : []),
    [csv]
  )
  const { groups, rootVariables } = useMemo(
    () => groupVariables(variables),
    [variables]
  )

  const refetch = useCallback(() => {
    void doFetch(true)
  }, [doFetch])

  return {
    varType,
    setVarType,
    labelStyle,
    setLabelStyle,
    variables,
    groups,
    rootVariables,
    isLoading: isFetchingDatamapCSV,
    error,
    refetch,
  }
}
